/**
 * Wire-format DTOs — exact JSON shapes the NestJS server emits.
 *
 * These schemas are the parse-at-the-boundary contract for every
 * `axios` call made by `@repo/queries`. They are deliberately
 * decoupled from the legacy `@repo/schemas` entity types (which were
 * tailored to the prototype `MockApiClient` and use Date / number for
 * fields the server actually serialises as ISO strings / decimal
 * strings).
 *
 * Conventions:
 *   - **Money**: `MoneyAmountSchema` is a decimal string of MINOR
 *     units (e.g. `"1025"` is $10.25). The server's
 *     `apps/server/src/common/money/MoneyAmount.ts` is the reference.
 *     Conversion to display strings is done by `@repo/domain/Money`.
 *   - **Dates**: ISO-8601 strings. Hooks should `new Date(value)` if
 *     the UI needs `Date`. Coerce-on-read is left to the consumer
 *     because most screens render via `Intl.DateTimeFormat` and never
 *     materialise a `Date`.
 *   - **Pagination**: `{ items: T[]; nextCursor?: string }` returned
 *     by every `list*` endpoint that supports pagination. Cursors are
 *     opaque server-issued ids — never inspect them client-side.
 *   - **Errors**: `{ error: { kind, message, code?, fields?, meta? } }`
 *     handled in the axios response interceptor (`createAxios.ts`).
 *     The schema lives here for tests and future contract work.
 *
 * If the server changes a route's shape, update these schemas first
 * and let TypeScript drive the cascade through the hooks.
 */
import { z } from "zod";

// ---------------------------------------------------------------------------
// Primitives
// ---------------------------------------------------------------------------

/**
 * Decimal string of minor units. Mirrors
 * `apps/server/src/common/money/MoneyAmount.ts#MoneyAmountSchema`.
 *
 * Negative values are permitted because refunds and adjustments can
 * legitimately be negative on certain endpoints (e.g. payment net
 * computation echoes back signed amounts).
 */
export const MoneyAmountSchema = z.string().regex(/^-?\d+$/u, {
  message: "Amount must be a decimal string of minor units",
});
export type MoneyAmount = z.infer<typeof MoneyAmountSchema>;

/** ISO-8601 datetime string. */
export const IsoDateTimeSchema = z.string().min(1);
export type IsoDateTime = z.infer<typeof IsoDateTimeSchema>;

/** User currency code echoed by the server: 1-3 uppercase letters. */
export const CurrencyCodeSchema = z.string().regex(/^[A-Z]{1,3}$/u, {
  message: "Currency must be 1-3 uppercase letters",
});
export type CurrencyCode = z.infer<typeof CurrencyCodeSchema>;

export const EntityIdSchema = z.string().min(1);
export type EntityId = z.infer<typeof EntityIdSchema>;

// ---------------------------------------------------------------------------
// Pagination + error envelope
// ---------------------------------------------------------------------------

/**
 * Build a paginated response schema parametrised by item type.
 *
 * The server emits `{ items, nextCursor? }` for every list endpoint
 * that supports cursor pagination. `nextCursor` is omitted (not
 * `null`) when the page is the last one — `useInfiniteQuery` should
 * use that absence to stop fetching.
 */
export function paginated<TItem extends z.ZodTypeAny>(item: TItem) {
  return z.object({
    items: z.array(item),
    nextCursor: z.string().optional(),
  });
}

export type Page<TItem> = {
  items: TItem[];
  nextCursor?: string;
};

export const PaginationQuerySchema = z.object({
  limit: z.number().int().min(1).max(200).optional(),
  cursor: z.string().optional(),
});
export type PaginationQuery = z.infer<typeof PaginationQuerySchema>;

export const ErrorEnvelopeSchema = z.object({
  error: z.object({
    kind: z.string(),
    message: z.string(),
    code: z.string().optional(),
    fields: z.record(z.array(z.string())).optional(),
    meta: z.record(z.unknown()).optional(),
  }),
});
export type ErrorEnvelope = z.infer<typeof ErrorEnvelopeSchema>;

// ---------------------------------------------------------------------------
// Auth + users
// ---------------------------------------------------------------------------

export const UserSchema = z.object({
  id: z.string().min(1),
  email: z.string().email(),
  firstName: z.string().nullable().optional(),
  lastName: z.string().nullable().optional(),
  avatarUrl: z.string().nullable().optional(),
  timezone: z.string().min(1).optional(),
  locale: z.string().min(1).optional(),
  dateFormat: z.string().min(1).optional(),
  createdAt: IsoDateTimeSchema.optional(),
  updatedAt: IsoDateTimeSchema.optional(),
  /**
   * Mirrors SuperTokens' `EmailVerification.isEmailVerified()` at
   * the moment the response was built. Surfaced inside the auth
   * envelope (sign-up / sign-in) and `/auth/me` so the mobile
   * router can decide whether to gate the user behind the
   * verify-required screen on first render \u2014 without an
   * extra round-trip. Optional because the field only exists on
   * server responses; legacy persisted sessions don't carry it
   * and we don't want to invalidate them.
   */
  emailVerified: z.boolean().optional(),
});
export type User = z.infer<typeof UserSchema>;

/**
 * `/v1/auth/sign-up` and `/v1/auth/sign-in` envelopes. Bearer tokens
 * are duplicated in the body so mobile can stash them in SecureStore
 * in one round-trip; the SDK also caches them from response headers.
 */
export const AuthSessionSchema = z.object({
  userId: z.string().min(1),
  accessToken: z.string().optional(),
  refreshToken: z.string().optional(),
});
export type AuthSession = z.infer<typeof AuthSessionSchema>;

export const AuthEnvelopeSchema = z.object({
  user: UserSchema,
  session: AuthSessionSchema,
});
export type AuthEnvelope = z.infer<typeof AuthEnvelopeSchema>;

export const SignUpRequestSchema = z.object({
  email: z.string().email(),
  password: z.string().min(8),
  firstName: z.string().min(1).optional(),
  lastName: z.string().min(1).optional(),
});
export type SignUpRequest = z.infer<typeof SignUpRequestSchema>;

export const SignInRequestSchema = z.object({
  email: z.string().email(),
  password: z.string().min(1),
});
export type SignInRequest = z.infer<typeof SignInRequestSchema>;

/**
 * Slim shape returned by `GET /v1/auth/me`. Only includes identity
 * fields needed by the SessionProvider boot path. For full profile
 * data (name, locale, timezone, …) use `GET /v1/users/me` via
 * `useUserProfile()`.
 *
 * `emailVerified` is filled in by the server from the SuperTokens
 * EmailVerification recipe and surfaced here so mobile can drive
 * the "verify your email" banner without an extra round-trip.
 */
export const MeResponseSchema = z.object({
  user: z.object({
    id: z.string().min(1),
    email: z.string().email().optional(),
    emailVerified: z.boolean().optional(),
  }),
});
export type MeResponse = z.infer<typeof MeResponseSchema>;

/**
 * `POST /v1/auth/forgot-password` always returns 204 — the body is
 * empty on purpose (responding differently for known vs unknown
 * emails would let an attacker enumerate accounts). We expose the
 * request schema only.
 */
export const ForgotPasswordRequestSchema = z.object({
  email: z.string().email(),
});
export type ForgotPasswordRequest = z.infer<typeof ForgotPasswordRequestSchema>;

export const ResetPasswordRequestSchema = z.object({
  token: z.string().min(1),
  password: z.string().min(8),
});
export type ResetPasswordRequest = z.infer<typeof ResetPasswordRequestSchema>;

/**
 * `POST /v1/auth/send-email-verification` returns whether the user
 * was already verified (in which case no email was sent). The
 * caller can use that to surface a "you're already verified" state
 * instead of a generic "email sent" success.
 */
export const SendEmailVerificationResponseSchema = z.object({
  ok: z.literal(true),
  alreadyVerified: z.boolean(),
});
export type SendEmailVerificationResponse = z.infer<
  typeof SendEmailVerificationResponseSchema
>;

export const UpdateMeRequestSchema = z.object({
  firstName: z.string().min(1).nullable().optional(),
  lastName: z.string().min(1).nullable().optional(),
  avatarUrl: z.string().url().nullable().optional(),
  timezone: z.string().min(1).optional(),
  locale: z.string().min(2).optional(),
  dateFormat: z.string().min(1).optional(),
});
export type UpdateMeRequest = z.infer<typeof UpdateMeRequestSchema>;

export const SignOutAllResponseSchema = z.object({
  ok: z.literal(true),
  revoked: z.number().int().min(0),
});
export type SignOutAllResponse = z.infer<typeof SignOutAllResponseSchema>;

/**
 * `POST /v1/auth/change-password` request shape.
 *
 * Min length on `newPassword` matches sign-up + the SuperTokens
 * default policy. Server enforces both ("must differ from current"
 * and the SuperTokens password-policy regex) — these constraints
 * are only the first line of defence against obvious typos so the
 * server doesn't have to round-trip a `400` for a 0-length submit.
 */
export const ChangePasswordRequestSchema = z.object({
  currentPassword: z.string().min(1),
  newPassword: z.string().min(8),
});
export type ChangePasswordRequest = z.infer<typeof ChangePasswordRequestSchema>;

export const ChangePasswordResponseSchema = z.object({
  ok: z.literal(true),
  /**
   * How many other-device sessions were revoked as a side-effect.
   * Surfaced so the success copy can read "signed you out of N other
   * devices" — gives the user proof the safety mechanism actually
   * fired.
   */
  revokedOtherSessions: z.number().int().min(0),
});
export type ChangePasswordResponse = z.infer<typeof ChangePasswordResponseSchema>;

/**
 * Per-org rollup returned by `GET /v1/users/me/delete-preflight` and
 * (on the conflict path) by `DELETE /v1/users/me`.
 *
 * The mobile UI buckets these into "blocking" / "solo" / "member"
 * the same way the server does — see
 * `apps/server/src/users/users.service.ts#OwnedOrgPreflight` for the
 * canonical doc.
 */
export const DeleteMeOrgRowSchema = z.object({
  id: EntityIdSchema,
  name: z.string().min(1),
  memberCount: z.number().int().nonnegative(),
  ownerCount: z.number().int().nonnegative(),
});
export type DeleteMeOrgRow = z.infer<typeof DeleteMeOrgRowSchema>;

/**
 * Wire shape for `GET /v1/users/me/delete-preflight`. Pre-flight is
 * read-only; it tells the client which of the three branches the
 * UI should render BEFORE the user has typed anything to confirm:
 *
 *   - `blockingOrgs` non-empty → show the "resolve these first"
 *     screen. Confirm button stays disabled.
 *   - `blockingOrgs` empty AND `soloOrgs` non-empty → render the
 *     type-to-confirm form with a yellow data-loss warning listing
 *     the orgs that will be hard-deleted alongside the user.
 *   - all three empty → no orgs at all; the user is a tourist who
 *     never created or joined a workspace. Plain confirm flow.
 */
export const DeleteMePreflightResponseSchema = z.object({
  blockingOrgs: z.array(DeleteMeOrgRowSchema),
  soloOrgs: z.array(DeleteMeOrgRowSchema),
  memberOrgs: z.array(DeleteMeOrgRowSchema),
});
export type DeleteMePreflightResponse = z.infer<
  typeof DeleteMePreflightResponseSchema
>;

/**
 * Body for `DELETE /v1/users/me`. The user types their own email to
 * arm the action — server compares case-insensitively against the
 * on-file address. Surfaces any mismatch as a `ValidationError` on
 * the `confirmEmail` field so the form can highlight the offending
 * input.
 */
export const DeleteMeRequestSchema = z.object({
  confirmEmail: z.string().email(),
});
export type DeleteMeRequest = z.infer<typeof DeleteMeRequestSchema>;

/**
 * Successful response shape. Empty by intent — the client should
 * sign out and route back to the welcome screen on `ok: true`. The
 * server's 409-on-blocking-orgs branch surfaces as an `AppError`
 * with `code === 'OWNER_HAS_SHARED_ORGS'` and `meta.blockingOrgs`,
 * NOT as a `200 ok: false`. Keeping the success shape minimal keeps
 * the hook callsite a one-liner.
 */
export const DeleteMeResponseSchema = z.object({
  ok: z.literal(true),
});
export type DeleteMeResponse = z.infer<typeof DeleteMeResponseSchema>;

// ---------------------------------------------------------------------------
// Push tokens
// ---------------------------------------------------------------------------

/**
 * Mobile-platform discriminator. Drives per-OS push payload
 * tweaks server-side (currently identical, but keeps the door
 * open for FCM channel selection on Android etc.).
 */
export const PushPlatformSchema = z.enum(["ios", "android", "web"]);
export type PushPlatform = z.infer<typeof PushPlatformSchema>;

const ExpoPushTokenWireSchema = z
  .string()
  .min(1)
  .max(200)
  .refine(
    (value) =>
      value.startsWith("ExponentPushToken[") ||
      value.startsWith("ExpoPushToken["),
    "token must be an Expo push token (ExponentPushToken[…])",
  );

const WebPushTokenWireSchema = z.string().min(1).max(8192);

/**
 * `POST /v1/users/me/push-tokens` request shape.
 *
 * Mobile posts an Expo token; web posts `JSON.stringify(sub.toJSON())`.
 */
export const RegisterPushTokenRequestSchema = z.discriminatedUnion("platform", [
  z.object({ platform: z.literal("ios"), token: ExpoPushTokenWireSchema }),
  z.object({ platform: z.literal("android"), token: ExpoPushTokenWireSchema }),
  z.object({ platform: z.literal("web"), token: WebPushTokenWireSchema }),
]);
export type RegisterPushTokenRequest = z.infer<
  typeof RegisterPushTokenRequestSchema
>;

export const RegisterPushTokenResponseSchema = z.object({
  id: EntityIdSchema,
  token: z.string().min(1),
  platform: PushPlatformSchema,
  createdAt: IsoDateTimeSchema,
  lastSeenAt: IsoDateTimeSchema,
});
export type RegisterPushTokenResponse = z.infer<
  typeof RegisterPushTokenResponseSchema
>;

export const DeregisterPushTokenResponseSchema = z.object({
  deleted: z.number().int().nonnegative(),
});
export type DeregisterPushTokenResponse = z.infer<
  typeof DeregisterPushTokenResponseSchema
>;

// ---------------------------------------------------------------------------
// Feedback
// ---------------------------------------------------------------------------

export const FeedbackCategorySchema = z.enum(["idea", "bug", "praise"]);
export type FeedbackCategory = z.infer<typeof FeedbackCategorySchema>;

export const SubmitFeedbackRequestSchema = z.object({
  category: FeedbackCategorySchema,
  message: z.string().trim().min(6).max(4000),
  followUp: z.boolean().optional(),
  context: z.record(z.unknown()).optional(),
});
export type SubmitFeedbackRequest = z.infer<typeof SubmitFeedbackRequestSchema>;

export const SubmitFeedbackResponseSchema = z.object({
  ok: z.literal(true),
  receivedAt: z.string().min(1),
});
export type SubmitFeedbackResponse = z.infer<
  typeof SubmitFeedbackResponseSchema
>;

// ---------------------------------------------------------------------------
// Notifications
// ---------------------------------------------------------------------------

export const NotificationKindSchema = z.enum([
  "invoice.sent",
  "invoice.viewed",
  "invoice.paid",
  "invoice.partial",
  "invoice.overdue",
  "system",
]);
export type NotificationKind = z.infer<typeof NotificationKindSchema>;

export const NotificationSchema = z.object({
  id: EntityIdSchema,
  userId: z.string().min(1),
  orgId: EntityIdSchema.nullable(),
  kind: NotificationKindSchema,
  title: z.string().min(1),
  body: z.string().nullable(),
  metadata: z.record(z.unknown()).nullable(),
  readAt: IsoDateTimeSchema.nullable(),
  createdAt: IsoDateTimeSchema,
});
export type Notification = z.infer<typeof NotificationSchema>;

export const NotificationsListResponseSchema = z.object({
  items: z.array(NotificationSchema),
  nextCursor: z.string().nullable(),
});
export type NotificationsListResponse = z.infer<
  typeof NotificationsListResponseSchema
>;

export const UnreadCountResponseSchema = z.object({
  count: z.number().int().nonnegative(),
});
export type UnreadCountResponse = z.infer<typeof UnreadCountResponseSchema>;

export const MarkAllReadResponseSchema = z.object({
  updated: z.number().int().nonnegative(),
});
export type MarkAllReadResponse = z.infer<typeof MarkAllReadResponseSchema>;

// ---------------------------------------------------------------------------
// Organizations
// ---------------------------------------------------------------------------

export const MembershipRoleSchema = z.enum(["owner", "admin", "member"]);
export type MembershipRole = z.infer<typeof MembershipRoleSchema>;

const OrganizationCoreSchema = z.object({
  id: EntityIdSchema,
  name: z.string().min(1),
  nickname: z.string().min(1),
  description: z.string().nullable().optional(),
  currency: CurrencyCodeSchema,
  dateFormat: z.string().min(1),
  timezone: z.string().min(1),
  invoicePrefix: z.string().min(1),
  logoUrl: z.string().nullable().optional(),
  branding: z.record(z.unknown()).nullable().optional(),
  contact: z.record(z.unknown()).nullable().optional(),
  createdByUserId: z.string().min(1),
  archivedAt: IsoDateTimeSchema.nullable().optional(),
  /**
   * When this org will be permanently deleted (cascading clients,
   * invoices, payments, services, memberships, etc.). Computed by
   * the server as `archivedAt + 30 days` and only set when the row
   * is archived; live orgs return `null`.
   *
   * The mobile UI uses this to surface a countdown ("Permanently
   * deletes in N days") on the archived list so owners always know
   * how much grace period remains. Restoring the org clears it.
   */
  deleteAt: IsoDateTimeSchema.nullable().optional(),
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
});

/** Single org as returned by `GET /v1/orgs/:orgId` (full detail). */
export const OrganizationSchema = OrganizationCoreSchema.extend({
  /** Set on `GET /:orgId` only; encoded as a string because BIGINT. */
  nextInvoiceSeq: z.string().optional(),
  businessNumber: z.string().nullable().optional(),
  taxNumber: z.string().nullable().optional(),
  registrationNumber: z.string().nullable().optional(),
});
export type Organization = z.infer<typeof OrganizationSchema>;

/**
 * Cheap rollup the server attaches to every list row so the
 * biz-switcher can render counts + outstanding totals for orgs that
 * aren't currently active. `outstandingMinor` is a decimal string of
 * minor units (cents); the client parses it with `Money.fromMinor`.
 */
export const OrganizationStatsSchema = z.object({
  invoiceCount: z.number().int().nonnegative(),
  clientCount: z.number().int().nonnegative(),
  outstandingMinor: z.string().min(1),
  currency: CurrencyCodeSchema,
});
export type OrganizationStats = z.infer<typeof OrganizationStatsSchema>;

/**
 * Org as returned by `GET /v1/orgs` (list). Each row carries the
 * caller's `role` so screens can filter UI by membership without an
 * extra fetch, plus a `stats` rollup for at-a-glance switching.
 */
export const OrganizationListItemSchema = OrganizationCoreSchema.extend({
  role: MembershipRoleSchema,
  stats: OrganizationStatsSchema.optional(),
});
export type OrganizationListItem = z.infer<typeof OrganizationListItemSchema>;

export const OrganizationsListResponseSchema = paginated(
  OrganizationListItemSchema,
);
export type OrganizationsListResponse = z.infer<
  typeof OrganizationsListResponseSchema
>;

export const CreateOrganizationRequestSchema = z.object({
  name: z.string().min(1),
  nickname: z.string().min(1).optional(),
  description: z.string().optional(),
  currency: CurrencyCodeSchema.default("USD"),
  dateFormat: z.string().min(1).default("MM/DD/YYYY"),
  timezone: z.string().min(1).default("UTC"),
  invoicePrefix: z.string().min(1).default("INV"),
  logoUrl: z.string().url().nullable().optional(),
  branding: z.record(z.unknown()).optional(),
  contact: z.record(z.unknown()).optional(),
  businessNumber: z.string().optional(),
  taxNumber: z.string().optional(),
  registrationNumber: z.string().optional(),
});
export type CreateOrganizationRequest = z.infer<
  typeof CreateOrganizationRequestSchema
>;

/**
 * `currency` is allowed on update so the Defaults screen can switch
 * the org-level default. Existing invoices keep their stamped currency.
 */
export const UpdateOrganizationRequestSchema =
  CreateOrganizationRequestSchema.partial();
export type UpdateOrganizationRequest = z.infer<
  typeof UpdateOrganizationRequestSchema
>;

// ---------------------------------------------------------------------------
// Memberships + invites
// ---------------------------------------------------------------------------

export const MembershipSchema = z.object({
  orgId: EntityIdSchema,
  userId: z.string().min(1),
  role: MembershipRoleSchema,
  joinedAt: IsoDateTimeSchema.optional(),
  email: z.string().email().optional(),
  firstName: z.string().nullable().optional(),
  lastName: z.string().nullable().optional(),
});
export type Membership = z.infer<typeof MembershipSchema>;

export const MembershipsListResponseSchema = z.array(MembershipSchema);
export type MembershipsListResponse = z.infer<
  typeof MembershipsListResponseSchema
>;

export const UpdateMembershipRoleRequestSchema = z.object({
  role: z.enum(["admin", "member"]),
});
export type UpdateMembershipRoleRequest = z.infer<
  typeof UpdateMembershipRoleRequestSchema
>;

export const InviteSchema = z.object({
  id: EntityIdSchema,
  orgId: EntityIdSchema,
  email: z.string().email(),
  role: MembershipRoleSchema,
  invitedByUserId: z.string().min(1),
  expiresAt: IsoDateTimeSchema,
  acceptedAt: IsoDateTimeSchema.nullable().optional(),
  revokedAt: IsoDateTimeSchema.nullable().optional(),
  createdAt: IsoDateTimeSchema,
});
export type Invite = z.infer<typeof InviteSchema>;

export const InvitesListResponseSchema = z.array(InviteSchema);
export type InvitesListResponse = z.infer<typeof InvitesListResponseSchema>;

/**
 * `POST /v1/orgs/:orgId/invites` echoes the bare token alongside the
 * persisted invite row. Stage 1 stub mailer also logs an accept URL
 * containing the token to the server console.
 */
export const CreateInviteResponseSchema = z.object({
  invite: InviteSchema,
  token: z.string().min(1),
});
export type CreateInviteResponse = z.infer<typeof CreateInviteResponseSchema>;

export const CreateInviteRequestSchema = z.object({
  email: z.string().email(),
  role: z.enum(["admin", "member"]).default("member"),
});
export type CreateInviteRequest = z.infer<typeof CreateInviteRequestSchema>;

export const AcceptInviteRequestSchema = z.object({
  token: z.string().min(16),
});
export type AcceptInviteRequest = z.infer<typeof AcceptInviteRequestSchema>;

export const AcceptInviteResponseSchema = z.object({
  invite: InviteSchema,
  membership: MembershipSchema,
});
export type AcceptInviteResponse = z.infer<typeof AcceptInviteResponseSchema>;

/**
 * Public invite preview returned by `GET /v1/invites/preview/:token`.
 *
 * Slim by design — surfacing the recipient's email here would be a
 * privacy regression (the link landed in their inbox already; we
 * shouldn't reflect the address through every link-prefetcher in
 * the chain). Status is split into four explicit values so the UI
 * can render distinct empty states without parsing dates client-
 * side.
 */
export const InvitePreviewSchema = z.object({
  status: z.enum(["pending", "expired", "revoked", "accepted"]),
  orgName: z.string().min(1),
  inviterName: z.string().min(1),
  role: MembershipRoleSchema,
  expiresAt: IsoDateTimeSchema,
});
export type InvitePreview = z.infer<typeof InvitePreviewSchema>;

// ---------------------------------------------------------------------------
// Clients
// ---------------------------------------------------------------------------

export const ClientSchema = z.object({
  id: EntityIdSchema,
  orgId: EntityIdSchema,
  name: z.string().min(1),
  displayName: z.string().nullable().optional(),
  company: z.string().nullable().optional(),
  contact: z.record(z.unknown()),
  address: z.record(z.unknown()).nullable().optional(),
  notes: z.string().nullable().optional(),
  photoUrl: z.string().nullable().optional(),
  defaultTaxExempt: z.boolean(),
  taxId: z.string().nullable().optional(),
  isActive: z.boolean(),
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
});
export type Client = z.infer<typeof ClientSchema>;

export const ClientsListResponseSchema = paginated(ClientSchema);
export type ClientsListResponse = z.infer<typeof ClientsListResponseSchema>;

export const CreateClientRequestSchema = z.object({
  name: z.string().min(1),
  displayName: z.string().max(30).optional(),
  company: z.string().optional(),
  contact: z.record(z.unknown()).default({}),
  address: z.record(z.unknown()).optional(),
  notes: z.string().optional(),
  photoUrl: z.string().url().optional(),
  defaultTaxExempt: z.boolean().default(false),
  taxId: z.string().optional(),
  isActive: z.boolean().default(true),
});
export type CreateClientRequest = z.infer<typeof CreateClientRequestSchema>;

export const UpdateClientRequestSchema = CreateClientRequestSchema.partial();
export type UpdateClientRequest = z.infer<typeof UpdateClientRequestSchema>;

// ---------------------------------------------------------------------------
// Services (catalog items)
// ---------------------------------------------------------------------------

export const ServiceSchema = z.object({
  id: EntityIdSchema,
  orgId: EntityIdSchema,
  name: z.string().min(1),
  description: z.string().nullable().optional(),
  pricingRate: MoneyAmountSchema,
  pricingCurrency: CurrencyCodeSchema,
  pricingUnit: z.string().min(1),
  pricingCustomUnit: z.string().nullable().optional(),
  taxable: z.boolean(),
  /**
   * Server returns either an absent field or a numeric percentage
   * (e.g. `7.5` for 7.5%). Using number for ergonomics — the source
   * is a numeric column, not a money amount.
   */
  taxRate: z.number().min(0).max(100).optional(),
  tags: z.array(z.string()),
  isActive: z.boolean(),
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
});
export type Service = z.infer<typeof ServiceSchema>;

export const ServicesListResponseSchema = paginated(ServiceSchema);
export type ServicesListResponse = z.infer<typeof ServicesListResponseSchema>;

export const CreateServiceRequestSchema = z.object({
  name: z.string().min(1),
  description: z.string().optional(),
  pricingRate: MoneyAmountSchema,
  pricingCurrency: CurrencyCodeSchema,
  pricingUnit: z.string().min(1).default("fixed"),
  pricingCustomUnit: z.string().optional(),
  taxable: z.boolean().default(true),
  taxRate: z.number().min(0).max(100).optional(),
  tags: z.array(z.string()).default([]),
  isActive: z.boolean().default(true),
});
export type CreateServiceRequest = z.infer<typeof CreateServiceRequestSchema>;

export const UpdateServiceRequestSchema = CreateServiceRequestSchema.partial();
export type UpdateServiceRequest = z.infer<typeof UpdateServiceRequestSchema>;

// ---------------------------------------------------------------------------
// Tax options (per-org rate library)
// ---------------------------------------------------------------------------

/**
 * Distinguishes additive taxes (sales tax, VAT, GST — added on top of
 * the subtotal) from withholding-style "subtract" taxes (e.g. 1099
 * backup withholding) which are deducted from the total.
 */
export const TaxOptionKindSchema = z.enum(["add", "subtract"]);
export type TaxOptionKind = z.infer<typeof TaxOptionKindSchema>;

export const TaxOptionSchema = z.object({
  id: EntityIdSchema,
  orgId: EntityIdSchema,
  name: z.string().min(1),
  /** Server stores rate as `numeric(5,2)`; serialised as a string. */
  rate: z.string(),
  /** Defaults to 'add' for legacy rows so older clients keep working. */
  kind: TaxOptionKindSchema.default("add"),
  description: z.string().nullable().optional(),
  region: z.string().nullable().optional(),
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
});
export type TaxOption = z.infer<typeof TaxOptionSchema>;

export const TaxOptionsListResponseSchema = z.array(TaxOptionSchema);
export type TaxOptionsListResponse = z.infer<
  typeof TaxOptionsListResponseSchema
>;

export const CreateTaxOptionRequestSchema = z.object({
  name: z.string().min(1),
  rate: z.number().min(0).max(100),
  kind: TaxOptionKindSchema.optional(),
  description: z.string().optional(),
  region: z.string().optional(),
});
export type CreateTaxOptionRequest = z.infer<
  typeof CreateTaxOptionRequestSchema
>;

export const UpdateTaxOptionRequestSchema =
  CreateTaxOptionRequestSchema.partial();
export type UpdateTaxOptionRequest = z.infer<
  typeof UpdateTaxOptionRequestSchema
>;

// ---------------------------------------------------------------------------
// Invoices (heaviest entity — lots of nested wire types)
// ---------------------------------------------------------------------------

export const InvoiceStatusSchema = z.enum([
  "draft",
  "pending",
  "sent",
  "paid",
  "overdue",
  "cancelled",
]);
export type InvoiceStatus = z.infer<typeof InvoiceStatusSchema>;

/**
 * Discriminated union mirroring the server's
 * `apps/server/src/invoices/invoices.schemas.ts#TaxConfigSchema`.
 * Rates are decimal strings to preserve the per-jurisdiction precision
 * (e.g. `"7.5"`, `"19.6"`).
 */
export const TaxConfigSchema = z.discriminatedUnion("method", [
  z.object({ method: z.literal("none") }),
  z.object({
    method: z.literal("on_total"),
    rate: z.union([z.number(), z.string()]),
    inclusive: z.boolean().default(false),
    label: z.string().optional(),
  }),
  z.object({
    method: z.literal("per_item"),
    inclusive: z.boolean().default(false),
    label: z.string().optional(),
  }),
  z.object({
    method: z.literal("as_deduction"),
    rate: z.union([z.number(), z.string()]),
    label: z.string().optional(),
  }),
]);
export type TaxConfig = z.infer<typeof TaxConfigSchema>;

export const DiscountSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("percentage"),
    rate: z.union([z.number(), z.string()]),
  }),
  z.object({ type: z.literal("fixed"), amount: MoneyAmountSchema }),
]);
export type Discount = z.infer<typeof DiscountSchema>;

export const InvoiceTotalsSchema = z.object({
  subtotal: MoneyAmountSchema,
  discountTotal: MoneyAmountSchema,
  taxTotal: MoneyAmountSchema,
  total: MoneyAmountSchema,
  amountPaid: MoneyAmountSchema,
  amountDue: MoneyAmountSchema,
});
export type InvoiceTotals = z.infer<typeof InvoiceTotalsSchema>;

/**
 * Frozen branding fields captured the moment an invoice transitions
 * out of `draft` (the customer's first encounter with it). The
 * renderer prefers this over the live `organization` row so editing
 * the org's logo / accent / contact in `/settings/design` later
 * doesn't retroactively rewrite the invoice the customer already
 * received.
 *
 * Always optional / nullable on the wire — `null` on every draft and
 * on legacy rows issued before the column existed. The renderer
 * treats `null` as "fall back to the live org".
 *
 * The shape mirrors the subset of `OrganizationSchema` that any
 * template ever reads. If a template starts depending on a new org
 * field, add it here AND extend
 * `apps/server/src/db/schema/invoices.ts#InvoiceBrandingSnapshot`
 * AND extend `freezeBrandingSnapshot()` in
 * `apps/server/src/invoices/branding-snapshot.ts`. Otherwise the
 * snapshot will silently drift from what the renderer needs.
 */
export const BrandingSnapshotSchema = z.object({
  name: z.string().min(1),
  description: z.string().nullable().optional(),
  logoUrl: z.string().nullable().optional(),
  branding: z.record(z.unknown()).nullable().optional(),
  contact: z.record(z.unknown()).nullable().optional(),
  taxNumber: z.string().nullable().optional(),
  businessNumber: z.string().nullable().optional(),
  registrationNumber: z.string().nullable().optional(),
  /** ISO 8601 — when the snapshot was first frozen onto the invoice. */
  frozenAt: IsoDateTimeSchema,
});
export type BrandingSnapshot = z.infer<typeof BrandingSnapshotSchema>;

/**
 * Build an "effective organization" for rendering by overlaying a
 * frozen snapshot onto the live row. Only the fields explicitly
 * captured in the snapshot are overridden — everything else
 * (currency, dateFormat, invoicePrefix, role, stats, etc.) keeps
 * its live value because those affect runtime behaviour rather than
 * what the customer sees on the printed invoice.
 *
 * This is the single source of truth for "what does the renderer
 * see for org X on invoice Y". Both the server printer pipeline and
 * the mobile preview WebView must call this BEFORE handing the org
 * to a template, otherwise drafts and sent invoices will diverge.
 */
export function applyBrandingSnapshot<
  O extends {
    branding?: unknown;
    logoUrl?: unknown;
    contact?: unknown;
    name?: unknown;
    description?: unknown;
    taxNumber?: unknown;
    businessNumber?: unknown;
    registrationNumber?: unknown;
  },
>(org: O, snapshot: BrandingSnapshot | null | undefined): O {
  if (!snapshot) return org;
  return {
    ...org,
    name: snapshot.name,
    description: snapshot.description ?? null,
    logoUrl: snapshot.logoUrl ?? null,
    branding: (snapshot.branding ?? null) as O["branding"],
    contact: (snapshot.contact ?? null) as O["contact"],
    taxNumber: snapshot.taxNumber ?? null,
    businessNumber: snapshot.businessNumber ?? null,
    registrationNumber: snapshot.registrationNumber ?? null,
  };
}

export const InvoiceLineItemSchema = z.object({
  id: EntityIdSchema,
  invoiceId: EntityIdSchema.optional(),
  serviceId: EntityIdSchema.nullable().optional(),
  position: z.number().int().min(0).optional(),
  description: z.string().min(1),
  quantity: z.string(),
  unitPrice: MoneyAmountSchema,
  unit: z.string().min(1),
  /**
   * On a stored line, the server emits `discount` + `discountType`
   * separately (legacy column shape). On create / update inputs we
   * accept the discriminated union from `DiscountSchema`. Hooks that
   * round-trip an invoice should map between the two representations.
   */
  discount: z.union([z.string(), z.number()]).nullable().optional(),
  discountType: z.enum(["percentage", "fixed"]).optional(),
  taxable: z.boolean(),
  taxRate: z.union([z.string(), z.number()]).nullable().optional(),
  notes: z.string().nullable().optional(),
});
export type InvoiceLineItem = z.infer<typeof InvoiceLineItemSchema>;

/**
 * On-invoice payment-method ids. NOT to be confused with
 * {@link PaymentMethodSchema} below, which is the analytics-style
 * tag stored on a recorded `payment` row (`bank_transfer`,
 * `credit_card`, etc.).
 *
 * `'stripe'` was removed in the 2026-05-09 alpha-readiness audit:
 * the option implied a live online checkout we never built, and
 * existing rows have been migrated by
 * `0017_drop_stripe_payment_methods`. `'paypal'` stays in the
 * enum but the mobile UI now treats it as a free-text option
 * (the user types their PayPal handle into `detail`); a future
 * real PayPal processor would land under a new id.
 */
export const InvoicePaymentMethodIdSchema = z.enum([
  "none",
  "paypal",
  "stripe-checkout",
  "bank-transfer",
  "cash",
  "check",
  "custom",
]);
export type InvoicePaymentMethodId = z.infer<typeof InvoicePaymentMethodIdSchema>;

export const InvoicePaymentMethodSchema = z.object({
  id: InvoicePaymentMethodIdSchema,
  label: z.string().min(1),
  detail: z.string().optional(),
});
export type InvoicePaymentMethod = z.infer<typeof InvoicePaymentMethodSchema>;

export const RenderSnapshotAssetSchema = z.object({
  role: z.enum(["organization.logo", "invoice.signature", "client.photo"]),
  sourceUrl: z.string().nullable().optional(),
  storageKey: z.string().nullable().optional(),
  url: z.string().nullable().optional(),
  contentHash: z.string().nullable().optional(),
  contentType: z.string().nullable().optional(),
});
export type RenderSnapshotAsset = z.infer<typeof RenderSnapshotAssetSchema>;

export const RenderSnapshotV1Schema = z
  .object({
    schemaVersion: z.literal(1),
    frozenAt: IsoDateTimeSchema,
    rendererVersion: z.string().min(1),
    templateId: z.string().min(1),
    invoice: z.record(z.unknown()),
    lineItems: z.array(InvoiceLineItemSchema),
    organization: z.record(z.unknown()),
    client: z.record(z.unknown()).nullable(),
    assets: z.array(RenderSnapshotAssetSchema).default([]),
  })
  .strict();
export type RenderSnapshotV1 = z.infer<typeof RenderSnapshotV1Schema>;

export const InvoiceDocumentStatusSchema = z.enum(["rendering", "ready", "failed"]);
export type InvoiceDocumentStatus = z.infer<typeof InvoiceDocumentStatusSchema>;

export const InvoiceFinalDocumentSchema = z.object({
  documentId: EntityIdSchema.nullable(),
  status: InvoiceDocumentStatusSchema.nullable(),
  pdfUrl: z.string().url().nullable(),
  previewUrl: z.string().url().nullable(),
  issuedAt: IsoDateTimeSchema.nullable(),
  /**
   * Failure metadata for the most recent render attempt. Both
   * fields are populated together when `status === 'failed'` and
   * cleared on the next successful render. Surfaced to the mobile
   * client so a stuck `pending`/`Sending` invoice has an
   * actionable error to show the user instead of an indefinite
   * spinner — see the 2026-05-04 incident write-up. The wire
   * schema treats them as optional so legacy server builds that
   * predate this column still parse.
   */
  errorCode: z.string().nullable().optional(),
  errorMessage: z.string().nullable().optional(),
  legacyMissingFinalDocument: z.boolean(),
});
export type InvoiceFinalDocument = z.infer<typeof InvoiceFinalDocumentSchema>;

// Phase 10 (plan.md M5) — `strict()` so an extra field the server
// ships in a future minor version surfaces as a parse error
// instead of being silently dropped by Zod. The contract test in
// `wire.test.ts` pins this; if the server adds a column, both
// the schema and the consumer must be updated in lockstep.
export const InvoiceSchema = z.object({
  id: EntityIdSchema,
  orgId: EntityIdSchema,
  clientId: EntityIdSchema,
  clientNameSnapshot: z.string(),
  invoiceNumber: z.string().min(1),
  status: InvoiceStatusSchema,
  currency: CurrencyCodeSchema,
  issueDate: IsoDateTimeSchema,
  dueDate: IsoDateTimeSchema,
  taxConfig: TaxConfigSchema,
  /**
   * The invoice-level discount rule the user saved. `null` (or
   * absent) means no invoice-level discount; the discount AMOUNT lives
   * in `totals.discountTotal` either way. Persisted in its own column
   * so `PATCH /invoices/:id` and payment recomputes can preserve it
   * without the caller having to re-send the rule on every write —
   * see `docs/plan.md` C1/C2/C3.
   */
  invoiceDiscount: DiscountSchema.nullable().optional(),
  paymentMethods: z.array(InvoicePaymentMethodSchema).optional(),
  totals: InvoiceTotalsSchema,
  /**
   * Frozen branding from the org at the moment of `draft → sent | pending`
   * transition. `null` on drafts (renderer reads live org) and on
   * legacy rows issued before this column existed. See
   * {@link BrandingSnapshotSchema} for the shape.
   */
  brandingSnapshot: BrandingSnapshotSchema.nullable().optional(),
  notes: z.string().nullable().optional(),
  terms: z.string().nullable().optional(),
  signature: z.string().nullable().optional(),
  /** Renderer template id — see `templates.ts` for the catalogue
   *  (`classic`, `modern`, `minimal`, `executive`, `studio`, `compact`). */
  templateId: z.string().min(1).optional(),
  sentAt: IsoDateTimeSchema.nullable().optional(),
  paidAt: IsoDateTimeSchema.nullable().optional(),
  viewedAt: IsoDateTimeSchema.nullable().optional(),
  archivedAt: IsoDateTimeSchema.nullable().optional(),
  /**
   * URL-safe random token minted on the first `→ sent` transition.
   * `null` for drafts and legacy rows. The hosted public-invoice page
   * reads this from the URL; mobile uses it to deep-link the
   * recipient view from the Send sheet.
   */
  publicShareToken: z.string().nullable().optional(),
  publicSharedAt: IsoDateTimeSchema.nullable().optional(),
  finalDocument: InvoiceFinalDocumentSchema.optional(),
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
  /** Only present on `GET /:invoiceId`, not on list rows. */
  lineItems: z.array(InvoiceLineItemSchema).optional(),
}).strict();
export type Invoice = z.infer<typeof InvoiceSchema>;

export const InvoicesListResponseSchema = paginated(InvoiceSchema);
export type InvoicesListResponse = z.infer<typeof InvoicesListResponseSchema>;

/**
 * Input shape for `POST /v1/orgs/:orgId/invoices`. Mirrors the server's
 * `InvoiceLineItemInputSchema`. `quantity` accepts either a number or
 * a string for form ergonomics — the server will coerce.
 */
export const CreateInvoiceLineItemRequestSchema = z.object({
  id: EntityIdSchema.optional(),
  serviceId: EntityIdSchema.nullable().optional(),
  description: z.string().min(1),
  quantity: z.union([z.number(), z.string()]).default(1),
  unitPrice: MoneyAmountSchema,
  unit: z.string().min(1).default("fixed"),
  discount: DiscountSchema.optional(),
  taxable: z.boolean().default(true),
  taxRate: z.union([z.number(), z.string()]).optional(),
  notes: z.string().optional(),
});
export type CreateInvoiceLineItemRequest = z.infer<
  typeof CreateInvoiceLineItemRequestSchema
>;

export const CreateInvoiceRequestSchema = z.object({
  clientId: EntityIdSchema,
  // Phase 10 (plan.md M2) — server reserves the number; the client
  // can no longer override it. Until there's a UI for manual
  // numbering with a server-side uniqueness pre-check, the wire
  // just refuses the field.
  status: z.enum(["draft", "pending", "sent"]).default("draft"),
  currency: CurrencyCodeSchema,
  issueDate: IsoDateTimeSchema,
  dueDate: IsoDateTimeSchema,
  tax: TaxConfigSchema.default({ method: "none" }),
  invoiceDiscount: DiscountSchema.optional(),
  lineItems: z.array(CreateInvoiceLineItemRequestSchema).min(1),
  paymentMethods: z.array(InvoicePaymentMethodSchema).default([]),
  notes: z.string().optional(),
  terms: z.string().optional(),
  signature: z.string().optional(),
  /** Renderer template id; defaults server-side to 'classic'. */
  templateId: z.string().min(1).optional(),
});
export type CreateInvoiceRequest = z.infer<typeof CreateInvoiceRequestSchema>;

export const UpdateInvoiceRequestSchema = CreateInvoiceRequestSchema.partial()
  .omit({
    // Status changes go through `POST /v1/orgs/:orgId/invoices/:id/transition`,
    // never through PATCH. Allowing it on PATCH was the bypass that
    // defeated the financial-immutability guard server-side
    // (sent → draft via update, then edit line items). The mobile
    // app's `buildCreateInvoiceWirePayload` is the only call site
    // that sets status, and it does so on create only.
    status: true,
  })
  // Override `invoiceDiscount` to also accept `null` on PATCH. Three
  // cases the server distinguishes:
  //   - field absent  → preserve the existing saved discount
  //   - explicit null → clear the saved discount
  //   - object value  → overwrite with the new rule
  // Without this override `.partial()` only widens to `optional`, which
  // collapses "absent" and "explicit null" into the same wire shape.
  .extend({
    invoiceDiscount: DiscountSchema.nullable().optional(),
  });
export type UpdateInvoiceRequest = z.infer<typeof UpdateInvoiceRequestSchema>;

// ---------------------------------------------------------------------------
// Guest import
// ---------------------------------------------------------------------------

export const GuestImportBusinessSchema = CreateOrganizationRequestSchema.extend({
  localId: EntityIdSchema,
});
export type GuestImportBusiness = z.infer<typeof GuestImportBusinessSchema>;

export const GuestImportClientSchema = CreateClientRequestSchema.extend({
  localId: EntityIdSchema,
});
export type GuestImportClient = z.infer<typeof GuestImportClientSchema>;

export const GuestImportServiceSchema = CreateServiceRequestSchema.extend({
  localId: EntityIdSchema,
});
export type GuestImportService = z.infer<typeof GuestImportServiceSchema>;

export const GuestImportTaxOptionSchema = CreateTaxOptionRequestSchema.extend({
  localId: EntityIdSchema,
});
export type GuestImportTaxOption = z.infer<typeof GuestImportTaxOptionSchema>;

export const GuestImportInvoiceLineItemSchema =
  CreateInvoiceLineItemRequestSchema.extend({
    localId: EntityIdSchema,
    serviceLocalId: EntityIdSchema.nullable().optional(),
  }).omit({
    id: true,
    serviceId: true,
  });
export type GuestImportInvoiceLineItem = z.infer<
  typeof GuestImportInvoiceLineItemSchema
>;

export const GuestImportInvoiceSchema = CreateInvoiceRequestSchema.extend({
  localId: EntityIdSchema,
  clientLocalId: EntityIdSchema,
  lineItems: z.array(GuestImportInvoiceLineItemSchema).min(1),
}).omit({
  clientId: true,
  status: true,
});
export type GuestImportInvoice = z.infer<typeof GuestImportInvoiceSchema>;

export const GuestImportRequestSchema = z.object({
  importId: EntityIdSchema,
  guestBusinessId: EntityIdSchema,
  business: GuestImportBusinessSchema,
  clients: z.array(GuestImportClientSchema).max(100),
  services: z.array(GuestImportServiceSchema).max(100),
  taxOptions: z.array(GuestImportTaxOptionSchema).max(100),
  invoices: z.array(GuestImportInvoiceSchema).max(100),
});
export type GuestImportRequest = z.infer<typeof GuestImportRequestSchema>;

const IdMapSchema = z.record(EntityIdSchema);

export const GuestImportResponseSchema = z.object({
  importedBusinessId: EntityIdSchema,
  businessIdMap: IdMapSchema,
  clientIdMap: IdMapSchema,
  serviceIdMap: IdMapSchema,
  taxOptionIdMap: IdMapSchema,
  invoiceIdMap: IdMapSchema,
  invoiceLineItemIdMap: IdMapSchema,
});
export type GuestImportResponse = z.infer<typeof GuestImportResponseSchema>;

export const TransitionInvoiceRequestSchema = z.object({
  to: InvoiceStatusSchema,
});
export type TransitionInvoiceRequest = z.infer<
  typeof TransitionInvoiceRequestSchema
>;

export const ReserveInvoiceNumberResponseSchema = z.object({
  invoiceNumber: z.string().min(1),
});
export type ReserveInvoiceNumberResponse = z.infer<
  typeof ReserveInvoiceNumberResponseSchema
>;

export const InvoiceActivitySchema = z.object({
  id: EntityIdSchema,
  // Phase 7 (plan.md H3) — both FKs are now `set null` on the
  // server. Activity rows survive a hard-delete of the parent
  // invoice or org; the snapshots below carry the readable
  // history forward when they do.
  invoiceId: EntityIdSchema.nullable(),
  orgId: EntityIdSchema.nullable(),
  invoiceIdSnapshot: EntityIdSchema,
  orgIdSnapshot: EntityIdSchema,
  type: z.string(),
  description: z.string(),
  metadata: z.record(z.unknown()).nullable().optional(),
  performedByUserId: z.string().nullable().optional(),
  invoiceNumberSnapshot: z.string(),
  clientNameSnapshot: z.string().nullable().optional(),
  createdAt: IsoDateTimeSchema,
});
export type InvoiceActivity = z.infer<typeof InvoiceActivitySchema>;

export const InvoiceActivitiesListResponseSchema = paginated(
  InvoiceActivitySchema,
);
export type InvoiceActivitiesListResponse = z.infer<
  typeof InvoiceActivitiesListResponseSchema
>;

export const InvoiceSendRequestStatusSchema = z.enum([
  "queued",
  "rendering",
  "sending",
  "sent",
  "failed",
]);
export type InvoiceSendRequestStatus = z.infer<typeof InvoiceSendRequestStatusSchema>;

export const InvoiceSendRequestSchema = z.object({
  id: EntityIdSchema,
  orgId: EntityIdSchema,
  invoiceId: EntityIdSchema,
  invoiceDocumentId: EntityIdSchema,
  requestedByUserId: z.string().nullable().optional(),
  recipient: z.string().email(),
  message: z.string().nullable().optional(),
  status: InvoiceSendRequestStatusSchema,
  providerMessageId: z.string().nullable().optional(),
  errorCode: z.string().nullable().optional(),
  errorMessage: z.string().nullable().optional(),
  sentAt: IsoDateTimeSchema.nullable().optional(),
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
});
export type InvoiceSendRequest = z.infer<typeof InvoiceSendRequestSchema>;

export const SendInvoiceResponseSchema = z.object({
  invoice: InvoiceSchema,
  sendRequest: InvoiceSendRequestSchema,
  finalDocument: InvoiceFinalDocumentSchema,
});
export type SendInvoiceResponse = z.infer<typeof SendInvoiceResponseSchema>;

/**
 * Tone selection for `POST /invoices/:id/remind`. Mirrored from the
 * `draftReminder` AI tool so the operator's pre-Send choice carries
 * through to the email subject + body (kicker, accent, opening).
 */
export const InvoiceReminderToneSchema = z.enum(['friendly', 'firm', 'final']);
export type InvoiceReminderTone = z.infer<typeof InvoiceReminderToneSchema>;

/**
 * Body for `POST /v1/orgs/:orgId/invoices/:invoiceId/remind`.
 *
 * Why a separate request shape from {@link SendInvoiceSchema}:
 * a reminder MUST render a different template, ship a different
 * subject ("Reminder: …"), log a different activity kind, and skip
 * the status-transition machinery. Sharing the send schema with a
 * `kind` flag would conflate those rules into one branchy path on
 * both client and server. Distinct DTOs keep the contracts crisp.
 */
export const RemindInvoiceRequestSchema = z.object({
  /** Operator-selected tone (matches the AI reminder card UI). */
  tone: InvoiceReminderToneSchema,
  /** Optional override for the client's on-file email. */
  recipient: z.string().email().optional(),
  /** Optional sender note that lands in the reminder body. */
  message: z.string().trim().max(2000).optional(),
});
export type RemindInvoiceRequest = z.infer<typeof RemindInvoiceRequestSchema>;

/**
 * Response for `POST /v1/orgs/:orgId/invoices/:invoiceId/remind`.
 *
 * Minimal by design — a reminder doesn't move status or mint a new
 * `InvoiceSendRequest` row, so we just return the invoice (so caches
 * stay consistent if the server back-fills `lastReminderAt`-style
 * fields later) and the timestamp of this delivery.
 */
export const RemindInvoiceResponseSchema = z.object({
  invoice: InvoiceSchema,
  /** When this reminder was handed off to the mail provider. */
  sentAt: IsoDateTimeSchema,
  /** Tone that was actually used to render the email. */
  tone: InvoiceReminderToneSchema,
});
export type RemindInvoiceResponse = z.infer<typeof RemindInvoiceResponseSchema>;

// ---------------------------------------------------------------------------
// Payments
// ---------------------------------------------------------------------------

export const PaymentMethodSchema = z.enum([
  "bank_transfer",
  "credit_card",
  "debit_card",
  "cash",
  "check",
  "paypal",
  "stripe",
  "wire_transfer",
  "online_banking",
  "mobile_payment",
  "cryptocurrency",
  "other",
]);
export type PaymentMethod = z.infer<typeof PaymentMethodSchema>;

export const PaymentTypeSchema = z.enum([
  "payment",
  "refund",
  "adjustment",
  "fee",
  "discount",
]);
export type PaymentType = z.infer<typeof PaymentTypeSchema>;

export const PaymentStatusSchema = z.enum([
  "pending",
  "completed",
  "failed",
  "cancelled",
  "refunded",
  "disputed",
]);
export type PaymentStatus = z.infer<typeof PaymentStatusSchema>;

export const PaymentSchema = z.object({
  id: EntityIdSchema,
  orgId: EntityIdSchema,
  invoiceId: EntityIdSchema,
  amount: MoneyAmountSchema,
  currency: CurrencyCodeSchema,
  method: PaymentMethodSchema,
  type: PaymentTypeSchema,
  status: PaymentStatusSchema,
  paymentDate: IsoDateTimeSchema,
  transactionId: z.string().nullable().optional(),
  reference: z.string().nullable().optional(),
  notes: z.string().nullable().optional(),
  refundedPaymentId: EntityIdSchema.nullable().optional(),
  refundReason: z.string().nullable().optional(),
  processingFee: MoneyAmountSchema.nullable().optional(),
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
});
export type Payment = z.infer<typeof PaymentSchema>;

export const PaymentsListResponseSchema = paginated(PaymentSchema);
export type PaymentsListResponse = z.infer<typeof PaymentsListResponseSchema>;

export const PaymentSummarySchema = z.object({
  totalPaid: MoneyAmountSchema,
  totalRefunded: MoneyAmountSchema,
  netPaid: MoneyAmountSchema,
  lastPaymentDate: IsoDateTimeSchema.optional(),
  lastPaymentAmount: MoneyAmountSchema.optional(),
  lastPaymentMethod: PaymentMethodSchema.optional(),
  paymentCount: z.number().int().min(0),
  refundCount: z.number().int().min(0),
});
export type PaymentSummary = z.infer<typeof PaymentSummarySchema>;

export const CreatePaymentRequestSchema = z.object({
  amount: MoneyAmountSchema,
  currency: CurrencyCodeSchema,
  method: PaymentMethodSchema,
  type: PaymentTypeSchema.default("payment"),
  status: PaymentStatusSchema.default("completed"),
  paymentDate: IsoDateTimeSchema.optional(),
  transactionId: z.string().optional(),
  reference: z.string().optional(),
  notes: z.string().optional(),
  refundedPaymentId: EntityIdSchema.optional(),
  refundReason: z.string().optional(),
  processingFee: MoneyAmountSchema.optional(),
});
export type CreatePaymentRequest = z.infer<typeof CreatePaymentRequestSchema>;

export const UpdatePaymentRequestSchema = CreatePaymentRequestSchema.partial();
export type UpdatePaymentRequest = z.infer<typeof UpdatePaymentRequestSchema>;

// ---------------------------------------------------------------------------
// Org defaults + catalog
// ---------------------------------------------------------------------------

export const OrgDefaultsSchema = z.object({
  orgId: EntityIdSchema,
  settings: z.record(z.unknown()),
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
});
export type OrgDefaults = z.infer<typeof OrgDefaultsSchema>;

export const PatchOrgDefaultsRequestSchema = z.record(z.unknown());
export type PatchOrgDefaultsRequest = z.infer<
  typeof PatchOrgDefaultsRequestSchema
>;

export const SupportedCurrencySchema = z.object({
  code: CurrencyCodeSchema,
  name: z.string().min(1),
  symbol: z.string().min(1),
  symbolPosition: z.string().min(1),
  scale: z.number().int().min(0),
  isPopular: z.boolean(),
});
export type SupportedCurrency = z.infer<typeof SupportedCurrencySchema>;

export const SupportedCurrenciesResponseSchema = z.object({
  items: z.array(SupportedCurrencySchema),
});
export type SupportedCurrenciesResponse = z.infer<
  typeof SupportedCurrenciesResponseSchema
>;

export const BusinessTypeSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  description: z.string().min(1),
  category: z.string().min(1),
  defaults: z.record(z.unknown()),
});
export type BusinessType = z.infer<typeof BusinessTypeSchema>;

export const BusinessTypesResponseSchema = z.object({
  items: z.array(BusinessTypeSchema),
});
export type BusinessTypesResponse = z.infer<typeof BusinessTypesResponseSchema>;

// ---------------------------------------------------------------------------
// Printer / render pipeline
// ---------------------------------------------------------------------------

/**
 * Render-job lifecycle. The server returns the same shape from both
 * the kick-off endpoint and the polling endpoint so the client only
 * has to learn one schema.
 *
 * - `ready`   - PDF + preview already exist (cache hit *or* job done).
 *               `pdfUrl` and `previewUrl` are set.
 * - `queued`  - Job accepted, no worker has started it yet. `jobId` set.
 * - `working` - A worker is rendering. `jobId` set.
 * - `failed`  - Render attempt finished unsuccessfully. `error` set.
 *               Caller may retry by re-POSTing.
 */
export const RenderJobStatusSchema = z.enum([
  "ready",
  "queued",
  "working",
  "failed",
]);
export type RenderJobStatus = z.infer<typeof RenderJobStatusSchema>;

export const RenderInvoiceResponseSchema = z.object({
  status: RenderJobStatusSchema,
  /** Set when status is `queued` / `working` / `failed`. */
  jobId: z.string().optional(),
  /** Set when status is `ready`. */
  pdfUrl: z.string().url().optional(),
  /** Set when status is `ready`. */
  previewUrl: z.string().url().optional(),
  /** Hash that identifies this exact render. Stable across retries. */
  contentHash: z.string().optional(),
  /** Set when status is `failed`. */
  error: z.string().optional(),
});
export type RenderInvoiceResponse = z.infer<typeof RenderInvoiceResponseSchema>;

export const RenderInvoiceRequestSchema = z
  .object({
    /** Override the default template. Optional — defaults to `classic`. */
    templateId: z.string().min(1).optional(),
    /**
     * Force a fresh render even if the cache row matches. Useful when
     * debugging template changes that don't alter the invoice JSON.
     */
    force: z.boolean().optional(),
  })
  .partial();
export type RenderInvoiceRequest = z.infer<typeof RenderInvoiceRequestSchema>;

// ---------------------------------------------------------------------------
// AI assistant
// ---------------------------------------------------------------------------

/**
 * Summary row returned by `GET /v1/orgs/:orgId/ai/conversations`. The
 * detail variant adds the `messages` blob — we keep the two split so
 * the inbox query doesn't have to ship every conversation's full
 * history over the wire.
 *
 * Money / token counters are integers (not strings) because they're
 * always small (an int4 column on the server side caps at ~2.1B
 * tokens, well above any realistic single-conversation total).
 * Cost is a decimal STRING to keep fractional-cent precision —
 * mirrors how `invoiceArtifacts.totalCostUsd` is shaped on the wire.
 */
export const AiConversationSummarySchema = z.object({
  id: z.string().min(1),
  title: z.string().nullable(),
  messageCount: z.number().int().nonnegative(),
  totalCostUsd: z.string().min(1),
  totalInputTokens: z.number().int().nonnegative(),
  totalOutputTokens: z.number().int().nonnegative(),
  lastUsedModel: z.string().nullable(),
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
});
export type AiConversationSummary = z.infer<typeof AiConversationSummarySchema>;

export const AiConversationListResponseSchema = z.object({
  items: z.array(AiConversationSummarySchema),
});
export type AiConversationListResponse = z.infer<
  typeof AiConversationListResponseSchema
>;

/**
 * Detail variant — adds the raw UIMessage[] blob the AI SDK uses
 * internally. We don't re-validate the inner shape here because
 * `useChat` on the client reconstructs from this verbatim and the
 * SDK already enforces its own runtime contract.
 */
export const AiConversationDetailSchema = AiConversationSummarySchema.extend({
  messages: z.array(z.unknown()),
});
export type AiConversationDetail = z.infer<typeof AiConversationDetailSchema>;

/**
 * `POST /v1/orgs/:orgId/ai/chat` request. We're deliberately loose
 * on `messages` — `useChat` owns the canonical shape; running another
 * zod parse here would just duplicate the AI SDK's runtime
 * validation (and break every time the SDK extends its part schema).
 */
export const AiChatRequestSchema = z.object({
  conversationId: z.string().min(1).optional(),
  messages: z.array(z.unknown()).max(200),
  /**
   * Per-conversation autoApply toggle. Off by default — mutating
   * tools (createInvoice, sendInvoice) require an explicit user
   * approval round trip. Long-running batch flows (e.g. "draft
   * invoices for all of last month's jobs") can opt in.
   */
  autoApply: z.boolean().optional(),
});
export type AiChatRequest = z.infer<typeof AiChatRequestSchema>;

/**
 * Soft-failure shape every tool returns when the user can recover by
 * action. Mirrors `apps/server/src/ai/tools/createTool.ts` —
 * exported here so the mobile client can pattern-match on
 * `errorCode` to render the right inline UI.
 */
export const AiToolErrorCodeSchema = z.enum([
  "PLAN_LIMIT",
  "NOT_FOUND",
  "CONFLICT",
  "VALIDATION",
  "NEEDS_APPROVAL",
  "TRANSIENT",
]);
export type AiToolErrorCode = z.infer<typeof AiToolErrorCodeSchema>;

// ---------------------------------------------------------------------------
// AI invoice plan (proposeInvoice tool output + commit endpoint)
// ---------------------------------------------------------------------------

/**
 * Single line item inside an AI-proposed invoice. Matches the shape
 * the server stages in PlanStore — keep this in sync with
 * `InvoicePlanPayload` in `apps/server/src/ai/tools/propose-invoice.tool.ts`.
 *
 * `unitPrice` is a minor-unit integer string (cents for USD/GBP/EUR,
 * yen for JPY). The mobile UI must format this with the workspace
 * currency before display — never echo the raw string to the user.
 */
export const InvoicePlanLineItemSchema = z.object({
  description: z.string().min(1),
  quantity: z.number().positive(),
  unit: z.string().min(1),
  unitPrice: z.string().regex(/^\d+$/, "minor-unit integer string"),
  taxable: z.boolean(),
});
export type InvoicePlanLineItem = z.infer<typeof InvoicePlanLineItemSchema>;

/**
 * Wire-stable v1 plan payload streamed back from `proposeInvoice`.
 * Bump the schema name (v2, v3) when fields change incompatibly so
 * the mobile client can pattern-match on the version it knows.
 */
export const InvoicePlanV1Schema = z.object({
  draft: z.object({
    clientId: z.string().uuid(),
    currency: CurrencyCodeSchema,
    issueDate: z.string(),
    dueDate: z.string(),
    lineItems: z.array(InvoicePlanLineItemSchema).min(1),
    notes: z.string().optional(),
  }),
  /**
   * Set when the proposal targets an EXISTING DRAFT invoice — on
   * commit, the server updates that row instead of creating a new
   * one. The mobile PlanCard uses this to show "Updates INV-006"
   * in the header so the user understands they're not duplicating.
   */
  target: z
    .object({
      invoiceId: z.string().uuid(),
      invoiceNumber: z.string().min(1),
    })
    .optional(),
  totals: z.object({
    /** All four are minor-unit integer strings. */
    subtotal: z.string(),
    discountTotal: z.string(),
    taxTotal: z.string(),
    total: z.string(),
  }),
  client: z.object({
    id: z.string().uuid(),
    name: z.string(),
  }),
  display: z.object({
    /** Pre-formatted "Tue, Apr 28" — for header/sub display only. */
    issueDate: z.string(),
    dueDate: z.string(),
  }),
});
export type InvoicePlanV1 = z.infer<typeof InvoicePlanV1Schema>;

// `CommitPlanResponseSchema` (the response body of the deleted
// `POST /plans/:planId/commit` endpoint) was removed in the v2
// SDK-native approval migration. The equivalent "we created
// `INV-NNN`" payload now rides inline as the resolved
// `tool-createInvoice` output part on the conversation tape; the
// client reads it from there. No REST round-trip, no wire type.

// ---------------------------------------------------------------------------
// Online payment processors (Stripe Connect v1)
// ---------------------------------------------------------------------------

export const PublicInvoiceCheckoutAvailabilitySchema = z.object({
  stripe: z.object({
    available: z.boolean(),
  }),
});
export type PublicInvoiceCheckoutAvailability = z.infer<
  typeof PublicInvoiceCheckoutAvailabilitySchema
>;

export const StripeConnectStatusSchema = z.object({
  connected: z.boolean(),
  chargesEnabled: z.boolean(),
  detailsSubmitted: z.boolean(),
  onboardingComplete: z.boolean(),
  onboardingUrl: z.string().url().nullable().optional(),
});
export type StripeConnectStatus = z.infer<typeof StripeConnectStatusSchema>;

export const StartStripeConnectResponseSchema = z.object({
  url: z.string().url(),
});
export type StartStripeConnectResponse = z.infer<
  typeof StartStripeConnectResponseSchema
>;

export const CreatePublicCheckoutRequestSchema = z.object({
  provider: z.literal('stripe'),
});
export type CreatePublicCheckoutRequest = z.infer<
  typeof CreatePublicCheckoutRequestSchema
>;

export const CreatePublicCheckoutResponseSchema = z.object({
  url: z.string().url(),
});
export type CreatePublicCheckoutResponse = z.infer<
  typeof CreatePublicCheckoutResponseSchema
>;
